'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import {
  getProviderColor,
  getProviderLogoPlaceholder,
} from '@/components/cms/blocks/FourDResultsView';
import { DatePicker } from '@/components/shared/date-picker';
import { useLotteryDraws } from '@/features/lottery/hooks/useLotteryDraws';
import type { LotteryResult } from '@/features/lottery/types';
import { LotteryPayout } from './lottery-payout';
import { MOCK_PAYOUT_TABS } from './lottery-payout-data';

type ProviderCode = 'TOTO' | 'MAGNUM' | 'DAMACAI';

function _getProviderHeaderClass(code: ProviderCode) {
  switch (code) {
    case 'TOTO':
      return 'bg-gradient-to-r from-[#1e1b4b] to-[#4c1d95] text-white';
    case 'MAGNUM':
      return 'bg-[#FBBF24] text-gray-900';
    case 'DAMACAI':
      return 'bg-[#153E90] text-white';
    default:
      return 'bg-gray-700 text-white';
  }
}

function _ProviderLogoPlaceholder({ code }: { code: ProviderCode }) {
  switch (code) {
    case 'TOTO':
      return (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border-2 border-red-600 bg-white text-[10px] font-bold text-red-600">
          TOTO
        </div>
      );
    case 'MAGNUM':
      return (
        <div className="flex h-9 w-12 shrink-0 items-center justify-center rounded bg-black text-[10px] font-bold text-amber-400">
          MAG
        </div>
      );
    case 'DAMACAI':
      return (
        <div className="flex h-9 w-12 shrink-0 items-center justify-center rounded bg-white text-[10px] font-bold text-blue-800">
          DMC
        </div>
      );
    default:
      return null;
  }
}

function FourDResultCard({ result }: { result: LotteryResult }) {
  result.providerCode === 'MAGNUM' ? 'text-gray-700/80' : 'text-white/70';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
      <div className="bg-[linear-gradient(to_right_top,_#801A9A_0%,_#AD7316_97%)] p-px rounded-xl">
        <div className="rounded-xl overflow-hidden border border-[#5d2b7d] bg-[#3B1157]">
          {/* Card Header */}
          <div
            className={`${getProviderColor(result.providerCode)} p-3 md:p-4 py-1.5 md:py-2 flex justify-between items-center`}
          >
            <div className="text-white">
              <h3 className="font-bold text-base md:text-lg">
                4D - {result.provider}
              </h3>
              <p className="text-[10px] md:text-xs opacity-90">{result.date}</p>
              <p className="text-[8px] md:text-[9px]">
                Draw No. {result.drawNo}
              </p>
            </div>
            <div className="scale-75 md:scale-100">
              {getProviderLogoPlaceholder(result.providerCode)}
            </div>
          </div>

          {/* Top Prizes */}
          <div className="p-3 md:p-4 space-y-2 md:space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-white font-medium text-sm md:text-base">
                1st Prize
              </span>
              <span className="text-white text-lg md:text-xl font-semibold">
                {result.prizes.first}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white font-medium text-sm md:text-base">
                2nd Prize
              </span>
              <span className="text-white text-lg md:text-xl font-semibold">
                {result.prizes.second}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white font-medium text-sm md:text-base">
                3rd Prize
              </span>
              <span className="text-white text-lg md:text-xl font-semibold">
                {result.prizes.third}
              </span>
            </div>
          </div>

          {/* Special Prizes */}
          <div className="bg-[#4C1D80] p-1.5 md:p-2 text-center text-xs md:text-sm text-gray-300">
            Special
          </div>
          <div className="p-2 md:p-3">
            <div className="grid grid-cols-5 gap-y-1.5 gap-x-1 text-center">
              {result.special.map((num, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: mock results
                  key={`special-${i}-${num}`}
                  className="text-white text-[13px] md:text-base tracking-[0.1em] md:tracking-wider"
                >
                  {num}
                </div>
              ))}
            </div>
          </div>

          {/* Consolation Prizes */}
          <div className="bg-[#4C1D80] p-1.5 md:p-2 text-center text-xs md:text-sm text-gray-300 border-y border-[#5d2b7d]/50">
            Consolation
          </div>
          <div className="p-2 md:p-3">
            <div className="grid grid-cols-5 gap-y-1.5 gap-x-1 text-center">
              {result.consolation.map((num, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: mock results
                  key={`consolation-${i}-${num}`}
                  className="text-white text-[13px] md:text-base tracking-[0.1em] md:tracking-wider"
                >
                  {num}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const CategoryContentLottery = () => {
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState<'draw' | 'payout'>('draw');
  // Empty means "whatever the latest published draw date is"; the backend
  // resolves it and reports back which date it served.
  const [selectedDate, setSelectedDate] = useState('');

  const { data, isPending, isError, refetch } = useLotteryDraws(
    selectedDate || undefined
  );
  const results = data?.results ?? [];

  return (
    <div className="mb-4 px-2 mt-2 text-white no-scrollbar">
      <div className="mb-4 flex min-h-11 flex-row justify-between gap-2 rounded-lg border border-purple-500/30 bg-[#3B1157]/40 overflow-hidden sm:flex-row sm:items-stretch">
        <div className="flex  shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('draw')}
            className={`shrink-0 px-2 py-1.5 text-[11px] font-medium transition-colors sm:px-8 sm:text-xs ${
              activeTab === 'draw'
                ? 'rounded-l-lg bg-[#7E22CE] text-white'
                : 'border border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Draw Result
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('payout')}
            className={`flex-1 border-l border-white/10 px-2 py-2 text-[11px] font-medium transition-colors sm:px-8 sm:text-xs ${
              activeTab === 'payout'
                ? 'bg-[#7E22CE] text-white'
                : 'border border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Payout
          </button>
        </div>
        {/* <div className="hidden min-w-0 flex-1 sm:block" aria-hidden /> */}
        <div className="flex items-center gap-1 pr-1">
          <div className="shrink-0 text-[11px] flex items-center pr-2 text-gray-400 sm:text-xs border-r border-white/10">
            Draw Date
          </div>

          <DatePicker
            mode="single"
            value={selectedDate || data?.drawDate || ''}
            onChange={val => setSelectedDate(val as string)}
            triggerClassName="border-0 bg-transparent hover:bg-white/10 px-1 py-1 text-xs text-white"
          />
        </div>
      </div>

      {activeTab === 'draw' ? (
        <>
          {/* The requested date had no published draw, so an older one is shown. */}
          {data?.isFallback && (
            <p className="mb-3 text-center text-[11px] text-gray-400">
              {t('ui.lottery.showing_latest')}
            </p>
          )}
          {isPending ? (
            <p className="py-12 text-center text-xs text-gray-400">
              {t('ui.lottery.loading_results')}
            </p>
          ) : isError ? (
            /* A failed request is not the same as a tenant publishing nothing. */
            <div className="py-12 text-center text-xs text-gray-400">
              <p>{t('ui.lottery.load_failed')}</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-3 rounded-md border border-purple-500/40 px-4 py-1.5 text-white transition-colors hover:bg-[#7E22CE]"
              >
                {t('ui.lottery.retry')}
              </button>
            </div>
          ) : results.length === 0 ? (
            <p className="py-12 text-center text-xs text-gray-400">
              {t('ui.lottery.no_results')}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {results.map(result => (
                <FourDResultCard key={result.providerCode} result={result} />
              ))}
            </div>
          )}
        </>
      ) : (
        <LotteryPayout tabs={MOCK_PAYOUT_TABS} />
      )}
    </div>
  );
};
